import * as React from 'react';
import { useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import './image-marker.less';
import { calculateMarkerPosition } from './utils';

const DEFAULT_BUFFER = 12;
const INITIAL_SCALE = 1.0;

const ReactImageMarkerNotDraggable = forwardRef(({
  src,
  markers,
  onAddMarker,
  markerComponent: MarkerComponent,
  bufferLeft = DEFAULT_BUFFER,
  bufferTop = DEFAULT_BUFFER,
  alt = 'Markers',
  extraClass = '',
  triggerEmployeeDetailSidebar,
}, ref) => {
  const imageRef = useRef(null);
  const imageMarkerContainerRef = useRef(null);
  const resetTransformRef = useRef(null);
  const imageLoadTimeoutRef = useRef(null);
  const [isImageCentered, setIsImageCentered] = useState(false);
  const [computedInitialScale, setComputedInitialScale] = useState(INITIAL_SCALE);
  const zoomFns = useRef(null);

  useImperativeHandle(ref, () => ({
    zoomIn: () => zoomFns.current?.zoomIn(),
    zoomOut: () => zoomFns.current?.zoomOut(),
    resetTransform: () => zoomFns.current?.resetTransform(),
  }));

  // Handle window resize to recalculate scale
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      resizeTimeout = setTimeout(() => {
        if (imageRef.current && imageMarkerContainerRef.current) {
          const newScale = computeInitialScale();
          setComputedInitialScale(newScale);
          
          // Reset transform with new scale
          if (resetTransformRef.current && resetTransformRef.current.centerView) {
            resetTransformRef.current.centerView(newScale, 0);
          }
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  const computeInitialScale = () => {
    if (!imageRef.current || !imageMarkerContainerRef.current) {
      return INITIAL_SCALE;
    }

    const image = imageRef.current;
    const container = imageMarkerContainerRef.current;
    
    const imageWidth = image.naturalWidth;
    const imageHeight = image.naturalHeight;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    if (
      imageWidth === 0 ||
      imageHeight === 0 ||
      containerWidth === 0 ||
      containerHeight === 0
    ) {
      return INITIAL_SCALE;
    }

    // Calculate scale to fit image within container while maintaining aspect ratio
    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;

    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond original size

    // Add some padding (10% margin)
    return Math.max(scale * 0.9, 0.8); // Ensure it's not smaller than 0.8
  };

  const handleImageLoad = () => {
    // Compute the appropriate initial scale based on image and container dimensions
    const newScale = computeInitialScale();
    setComputedInitialScale(newScale);

    if (imageLoadTimeoutRef.current) {
      clearTimeout(imageLoadTimeoutRef.current);
    }
    imageLoadTimeoutRef.current = setTimeout(() => {
      imageLoadTimeoutRef.current = null;
      if (resetTransformRef.current && resetTransformRef.current.centerView) {
        // Set the scale and center the image
        resetTransformRef.current.centerView(newScale, 0);
      }
      setIsImageCentered(true);
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (imageLoadTimeoutRef.current) {
        clearTimeout(imageLoadTimeoutRef.current);
        imageLoadTimeoutRef.current = null;
      }
    };
  }, []);

  const handleImageClick = event => {
    if (!imageRef.current || !onAddMarker) {
      return;
    }
    const imageDimentions = imageRef.current.getBoundingClientRect();

    const [top, left] = calculateMarkerPosition(
      event,
      imageDimentions,
      window.scrollY,
      bufferLeft,
      bufferTop
    );

    onAddMarker({
      top,
      left,
    });
  };

  const handleMarkerClick = (event, marker) => {
    if (typeof triggerEmployeeDetailSidebar === 'function') {
      triggerEmployeeDetailSidebar(marker);
    }
  };

  const getItemPosition = marker => {
    const { top, left } = marker;
    return {
      top: `${top}%`,
      left: `${left}%`,
    };
  };

  return (
    <TransformWrapper
      wheel={{ step: 0.1, disabled: false }}
      minScale={0.2}
      maxScale={2.0}
      centerOnInit={true}
      limitToBounds={true}
      initialScale={computedInitialScale}
      panning={{ disabled: false }}
    >
      {({ zoomIn, zoomOut, resetTransform, centerView, ...rest }) => {
        // Save zoom functions for ref
        zoomFns.current = { zoomIn, zoomOut, resetTransform, centerView };
        // Store resetTransform function in ref for use in image onLoad
        resetTransformRef.current = {
          zoomIn,
          zoomOut,
          resetTransform,
          centerView
        };
        return (
          <TransformComponent
            wrapperStyle={{
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              height: '100%',
            }}
          >
            <div 
              className="image-marker"
              ref={imageMarkerContainerRef}
              style={{ visibility: isImageCentered ? 'visible' : 'hidden' }}
            >
              <img
                src={src}
                alt={alt}
                onClick={handleImageClick}
                onLoad={handleImageLoad}
                className={'image-marker__image ' + extraClass}
                ref={imageRef}
              />
              {markers.map((marker, itemNumber) => (
                <div
                  className={`image-marker__marker ${
                    !MarkerComponent && 'image-marker__marker--default'
                  }`}
                  key={itemNumber}
                  style={{ ...getItemPosition(marker), cursor: 'pointer' }}
                  data-testid="marker"
                  onClick={event => handleMarkerClick(event, marker)}
                >
                  {MarkerComponent ? (
                    <MarkerComponent {...marker} itemNumber={itemNumber} />
                  ) : (
                    itemNumber + 1
                  )}
                </div>
              ))}
            </div>
          </TransformComponent>
        );
      }}
    </TransformWrapper>
  );
});

ReactImageMarkerNotDraggable.defaultProps = {
  triggerEmployeeDetailSidebar: () => {},
};

ReactImageMarkerNotDraggable.displayName = 'ReactImageMarkerNotDraggable';

export default ReactImageMarkerNotDraggable;
